import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useCMS } from "@/hooks/useCMS";
import SEO from "@/components/SEO";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } }),
};

export default function Home() {
  const belowFoldRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const { content } = useCMS();
  const home = content.home || {};
  const social = content.social || {};
  const release = home.featured_release || {};

  const scrollDown = () => {
    belowFoldRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Extract Spotify track ID from a full URL or raw ID
  const getSpotifyTrackId = () => {
    const url = release.spotify_url || release.spotify_track_id || "";
    const match = url.match(/track\/([a-zA-Z0-9]+)/);
    return match ? match[1] : url;
  };
  const spotifyTrackId = getSpotifyTrackId();
  const spotifyUrl = release.spotify_url || `https://open.spotify.com/track/${spotifyTrackId}`;

  return (
    <main data-testid="home-page">
      <SEO path="/" />
      {/* ─── HERO ─── */}
      <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden flex items-center justify-center">
        {/* Video Background — static asset, no backend proxy */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/hero-poster.jpg"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full object-cover"
            style={{ pointerEvents: "none" }}
            src="/hero-bg.mp4"
          />
        </div>

        {/* Dark overlay — light at top, darker at bottom for text readability */}
        <div className="hero-overlay absolute inset-0 z-10" style={{ pointerEvents: "none" }} />

        {/* Hero Content */}
        <div className="relative z-20 text-center px-6">
          <motion.h1
            data-testid="hero-title"
            className="font-['Bungee'] text-7xl sm:text-8xl md:text-[10rem] lg:text-[12rem] text-[#F4EFE6] uppercase hero-title-stroke"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            PHÖÖN
          </motion.h1>

          <motion.p
            data-testid="hero-tagline"
            className="mt-6 md:mt-8 font-mono-label text-xs md:text-sm text-[#F4EFE6]/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            {home.tagline || "House & Tech House // Bay Area → Chicago"}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <Link
              to="/contact"
              data-testid="hero-cta"
              className="inline-block mt-8 md:mt-10 bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-xl uppercase tracking-wider px-10 py-3 hover:bg-[#FF5500]/80 transition-all duration-300"
            >
              Booking Inquiries
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <button
          data-testid="scroll-indicator"
          onClick={scrollDown}
          className="absolute bottom-8 z-20 animate-scroll-bounce text-[#FF5500]/60 hover:text-[#FF5500] transition-colors cursor-pointer"
        >
          <ChevronDown size={28} />
        </button>
      </section>

      {/* ─── BELOW THE FOLD ─── */}
      <section ref={belowFoldRef} className="py-24 md:py-32 lg:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* Latest Release Spotlight */}
          <motion.div
            data-testid="latest-release-section"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.p variants={fadeInUp} className="font-mono-label text-base text-[#FF5500] mb-8">
              Latest Release
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <motion.a href={spotifyUrl} target="_blank" rel="noopener noreferrer" variants={fadeInUp} className="border border-[#FF5500]/20 overflow-hidden block">
                <img
                  src={release.artwork_url ? `${BACKEND}${release.artwork_url}` : ""}
                  alt={`${release.title || "Release"} — artwork`}
                  className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700"
                  data-testid="latest-release-artwork"
                />
              </motion.a>

              <motion.div variants={fadeInUp} custom={1} className="flex flex-col justify-center">
                <h2 className="font-['Black_Han_Sans'] text-5xl md:text-7xl text-[#F4EFE6] uppercase">
                  {release.title || "I Was Like"}
                </h2>
                <p className="font-mono-label text-xs text-[#F4EFE6]/60 mt-3">{release.label} &middot; {release.year}</p>

                <div className="mt-8" data-testid="spotify-embed">
                  <iframe
                    style={{ borderRadius: "12px" }}
                    src={`https://open.spotify.com/embed/track/${spotifyTrackId}?utm_source=generator&theme=0`}
                    width="100%"
                    height="152"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title={`Phöön — ${release.title || ""}`}
                  />
                </div>

                <Link
                  to="/music"
                  className="mt-8 inline-flex items-center gap-2 font-mono-label text-xs text-[#FF5500] hover:text-[#F4EFE6] transition-colors group"
                  data-testid="view-all-music-link"
                >
                  View All Music
                  <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Photo Grid */}
          <motion.div
            className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeInUp}
          >
            {(home.photos || []).map((url, i) => {
              const src = url.startsWith("/") ? `${BACKEND}${url}` : url;
              return <img key={`photo-${i}`} src={src} alt="" className="w-full aspect-video object-cover border border-[#FF5500]/10" />;
            })}
          </motion.div>

          {/* Bio Pull */}
          <motion.div
            data-testid="bio-section"
            className="max-w-3xl mt-24 md:mt-32"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <p className="text-xl md:text-2xl leading-relaxed text-[#F4EFE6]/85 font-normal">
              {home.bio || ""}
            </p>
          </motion.div>

          {/* Video */}
          <motion.div
            className="mt-24 md:mt-32"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeInUp}
          >
            <div className="relative w-full aspect-video bg-[#0c1128] border border-[#FF5500]/20 overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${home.youtube_video_id || "UH4C71Raxdw"}?autoplay=1&mute=1&loop=1&playlist=${home.youtube_video_id || "UH4C71Raxdw"}&modestbranding=1&rel=0&playsinline=1`}
                title="Phöön Live"
                className="w-full h-full"
                style={{ border: "none" }}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            className="mt-24 md:mt-32"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Link
              to="/contact"
              data-testid="home-bottom-cta"
              className="inline-flex items-center gap-3 font-['Black_Han_Sans'] text-3xl md:text-4xl text-[#F4EFE6] hover:text-[#FF5500] transition-colors duration-300 group uppercase"
            >
              Book Phöön
              <span className="inline-block transform group-hover:translate-x-2 transition-transform duration-300">&rarr;</span>
            </Link>
          </motion.div>

          {/* Social Cards */}
          <motion.div
            className="mt-16 md:mt-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.p variants={fadeInUp} className="font-mono-label text-base text-[#FF5500] mb-8">
              Connect
            </motion.p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: "Instagram", handle: "@phoonmusic", href: "https://www.instagram.com/phoonmusic", icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10 md:w-14 md:h-14">
                    <rect x="2" y="2" width="20" height="20" rx="5" />
                    <circle cx="12" cy="12" r="5" />
                    <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
                  </svg>
                )},
                { name: "SoundCloud", handle: "@musicbyphoon", href: "https://soundcloud.com/musicbyphoon", icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-14 md:h-14">
                    <path d="M1 18V14h1v4H1zm2.5-5v5h1v-5h-1zM6 11v7h1v-7H6zm2.5-1v8h1v-8h-1zM11 8v10h1V8h-1zm2.5 1v9h1v-9h-1zM16 7v11h1V7h-1zm2.5 2v9h1V9h-1zM21 10v8h1v-8h-1z" />
                  </svg>
                )},
                { name: "Spotify", handle: "Phöön", href: "https://open.spotify.com/artist/4DKokEWRuxYx2XItcPgUQe?si=5wsBb6SoRNO6o52UbizzNg", icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-14 md:h-14">
                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 01-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 11-.277-1.215c3.809-.87 7.076-.496 9.712 1.115a.623.623 0 01.207.857zm1.225-2.718a.78.78 0 01-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 01-.452-1.492c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 01.255 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.935.935 0 11-.543-1.79c3.532-1.072 9.404-.865 13.115 1.338a.935.935 0 01-1.053 1.613z" />
                  </svg>
                )},
                { name: "YouTube", handle: "@phoonmusic", href: "https://www.youtube.com/@phoonmusic", icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-14 md:h-14">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                )},
              ].map((social, i) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  custom={i}
                  variants={fadeInUp}
                  data-testid={`home-social-${social.name.toLowerCase()}`}
                  className="bg-[#0c1128] border border-[#FF5500]/10 p-6 md:p-8 flex flex-col items-center text-center hover:border-[#FF5500]/40 transition-all duration-300 group"
                >
                  <div className="text-[#F4EFE6] group-hover:text-[#FF5500] transition-colors duration-300 mb-4 md:mb-6">
                    {social.icon}
                  </div>
                  <h3 className="font-['Black_Han_Sans'] text-lg md:text-xl text-[#F4EFE6] uppercase">
                    {social.name}
                  </h3>
                  <p className="font-mono-label text-[9px] md:text-[10px] text-[#FF5500]/60 mt-2">
                    {social.handle}
                  </p>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
